import { getUserDisplayName } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { Tooltip, User } from 'components/common';

import type { UserWithBasicData } from 'shared-types';

interface Props {
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'huge';
  users: UserWithBasicData[];
}

const Users = ({ size = 'medium', users }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const len = users.length;

  const numberOfRows =
    len <= 5
      ? len
      : len <= 6
      ? 3
      : len <= 8
      ? 4
      : len <= 10
      ? 5
      : len <= 12
      ? 4
      : 5;

  const gridTemplateRows = Array.from(Array(numberOfRows).keys())
    .map(() => '1fr')
    .join(' ');

  return (
    <div className={`users users-${size}`}>
      <Tooltip
        content={
          <div className="list">
            {users?.map((user) => (
              <User key={user.email} user={user} />
            ))}
          </div>
        }
        isLight={true}
      >
        <div className="inner">
          <span className="sr-only">
            {`${messages.commonUsers}: `}
            {users
              .map((user) =>
                getUserDisplayName({ messages, serverConfig, user })
              )
              .join(', ')}
          </span>
          {users?.slice(0, len > 3 ? 2 : 3).map((user) => (
            <div className="avatar" key={user.username}>
              <User
                showEmail={false}
                showNameOrUsername={false}
                size={size}
                user={user}
              />
            </div>
          ))}

          {len > 3 ? (
            <div className="avatar">
              <div className="more">{`+${len - 2}`}</div>
            </div>
          ) : null}
        </div>
      </Tooltip>

      <style jsx>{`
        .list {
          display: grid;
          gap: 10px 20px;
          grid-auto-flow: column;
          grid-template-rows: ${gridTemplateRows};
          padding: 10px 5px;
        }
        .inner {
          display: inline-flex;
        }
        .avatar {
          border: 2px solid white;
          border-radius: 100px;
          display: inline-flex;
          margin: -2px;
        }
        .avatar:not(:first-child) {
          margin-left: -5px;
        }
        .more {
          align-items: center;
          background-color: ${theme.lightGreyDark};
          border-radius: 100px;
          color: ${theme.textColorLight};
          display: inline-flex;
          flex-shrink: 0;
          font-size: 1.5rem;
          font-weight: 700;
          justify-content: center;
          line-height: 0;
          padding-right: 1px;
          width: 30px;
          height: 30px;
        }
        .users-tiny .more {
          font-size: 1.1rem;
          width: 20px;
          height: 20px;
        }
        .users-small .more {
          font-size: 1.3rem;
          width: 26px;
          height: 26px;
        }
        .users-medium .more {
          font-size: 1.5rem;
          width: 30px;
          height: 30px;
        }
        .users-large .more {
          font-size: 1.6rem;
          width: 34px;
          height: 34px;
        }
        .users-huge .more {
          font-size: 2.4rem;
          width: 64px;
          height: 64px;
        }
      `}</style>
    </div>
  );
};

export default Users;
